/**
 * This page is the list-view for object versions. When a single object is selected, it
 * becomes a rich table of versions. It is likely that we will want to outfit it
 * with features similar to the calls table. For example:
 * [ ] Add the ability to expand refs
 * [ ] Paginate & stream responses similar to calls
 * [ ] Add the ability to sort / filter on values
 * [ ] Add the ability to sort / filter on expanded values (blocked by general support for expansion operations)
 * [ ] Add sort / filter state to URL
 */
import {Button} from '@wandb/weave/components/Button';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import _ from 'lodash';
import React, {useMemo, useState} from 'react';
import {useHistory} from 'react-router-dom';

import {useWeaveflowCurrentRouteContext} from '../../context';
import {useShowDeleteButton} from '../common/DeleteModal';
import {SimplePageLayout} from '../common/SimplePageLayout';
import {useControllableState} from '../util';
import {
  DeleteObjectsButtonWithModal,
  DeleteObjectVersionsButtonWithModal,
} from './ObjectDeleteButtons';
import {WFHighLevelObjectVersionFilter} from './objectsPageTypes';
import {FilterableObjectVersionsTable} from './ObjectVersionsTable';

export const ObjectVersionsPage: React.FC<{
  entity: string;
  project: string;
  initialFilter?: WFHighLevelObjectVersionFilter;
  // Setting this will make the component a controlled component. The parent
  // is responsible for updating the filter.
  onFilterUpdate?: (filter: WFHighLevelObjectVersionFilter) => void;
}> = props => {
  const history = useHistory();
  const router = useWeaveflowCurrentRouteContext();
  const [filter, setFilter] = useControllableState(
    props.initialFilter ?? {},
    props.onFilterUpdate
  );
  const {entity, project} = props;
  const [selectedVersions, setSelectedVersions] = useState<string[]>([]);
  const onCompare = () => {
    history.push(router.compareObjectsUri(entity, project, selectedVersions));
  };

  const showDeleteButton = useShowDeleteButton(props.entity);
  const filteredOnObject = filter.objectName != null;
  const hasComparison = filteredOnObject;

  // Determine objectTitle based on the filter's baseObjectClass
  // Used for when we filter for Models
  const objectTitle = useMemo(() => {
    if (filter.baseObjectClass) {
      return _.capitalize(filter.baseObjectClass);
    }
    return 'Asset';
  }, [filter.baseObjectClass]);

  // Determine the base title
  const baseTitle = useMemo(() => {
    if (filter.objectName) {
      return 'Versions of ' + filter.objectName;
    } else if (filter.baseObjectClass) {
      return _.capitalize(filter.baseObjectClass) + 's';
    }
    return 'All assets';
  }, [filter.objectName, filter.baseObjectClass]);

  // Create title content with selection info and delete button
  const title = useMemo(() => {
    if (selectedVersions.length > 0) {
      const itemName = filter.baseObjectClass
        ? _.capitalize(filter.baseObjectClass).toLowerCase()
        : 'asset';
      const selectionText = `${selectedVersions.length} ${itemName}${
        selectedVersions.length === 1 ? '' : 's'
      } selected:`;

      return (
        <Tailwind>
          <div className="flex items-center gap-8">
            <span className="text-sm font-normal text-moon-500">
              {selectionText}
            </span>
            {showDeleteButton &&
              (filter.objectName ? (
                <DeleteObjectVersionsButtonWithModal
                  entity={entity}
                  project={project}
                  objectName={filter.objectName}
                  objectVersions={selectedVersions}
                  disabled={selectedVersions.length === 0}
                  onSuccess={() => setSelectedVersions([])}
                  buttonText="Delete"
                />
              ) : (
                <DeleteObjectsButtonWithModal
                  entity={entity}
                  project={project}
                  objectIds={selectedVersions.map(v => v.split(':')[0])}
                  disabled={selectedVersions.length === 0}
                  onSuccess={() => setSelectedVersions([])}
                  buttonText="Delete"
                />
              ))}
          </div>
        </Tailwind>
      );
    }

    return baseTitle;
  }, [
    filter.objectName,
    filter.baseObjectClass,
    selectedVersions,
    showDeleteButton,
    entity,
    project,
    setSelectedVersions,
    baseTitle,
  ]);

  return (
    <SimplePageLayout
      title={title}
      hideTabsIfSingle
      headerExtra={
        <ObjectVersionsPageHeaderExtra
          entity={entity}
          project={project}
          objectName={filter.objectName ?? null}
          selectedVersions={selectedVersions}
          setSelectedVersions={setSelectedVersions}
          showDeleteButton={showDeleteButton}
          showCompareButton={hasComparison}
          onCompare={onCompare}
          baseObjectClass={filter.baseObjectClass ?? undefined}
        />
      }
      tabs={[
        {
          label: '',
          content: (
            <FilterableObjectVersionsTable
              {...props}
              initialFilter={filter}
              onFilterUpdate={setFilter}
              selectedVersions={selectedVersions}
              setSelectedVersions={
                hasComparison || showDeleteButton
                  ? setSelectedVersions
                  : undefined
              }
              objectTitle={objectTitle}
            />
          ),
        },
      ]}
    />
  );
};

const ObjectVersionsPageHeaderExtra: React.FC<{
  entity: string;
  project: string;
  objectName: string | null;
  selectedVersions: string[];
  setSelectedVersions: (selected: string[]) => void;
  showDeleteButton?: boolean;
  showCompareButton?: boolean;
  onCompare: () => void;
  baseObjectClass?: string;
}> = ({
  entity,
  project,
  objectName,
  selectedVersions,
  setSelectedVersions,
  showDeleteButton,
  showCompareButton,
  onCompare,
  baseObjectClass,
}) => {
  const compareButton =
    showCompareButton && selectedVersions.length > 0 ? (
      <Button disabled={selectedVersions.length < 2} onClick={onCompare}>
        Compare
      </Button>
    ) : undefined;

  return (
    <Tailwind>
      <div className="mr-16 flex gap-8">{compareButton}</div>
    </Tailwind>
  );
};
